import PropTypes from "prop-types";
import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import {
  ArrowForward,
  ExpandMore,
  ExpandLess,
  CheckCircleOutline,
  ErrorOutline,
  Refresh,
  Search,
  StorefrontOutlined,
  WarningAmberOutlined,
  WidgetsOutlined,
} from "@mui/icons-material";
import DataTable from "shared/components/Tables/DataTable";
import { dashboardTableStyles } from "../../utils/dashboardTableStyles";
import { useTableroSac } from "../../hooks/useTableroSac";
import DashboardStatus from "../Cells/DashboardStatus";
import DashboardDetail from "../Modals/DashboardDetail";
import { formatDate, formatMinutes, STATUS } from "../../utils/dashboardModel";

const categories = {
  eds: "Estaciones de servicio",
  peajes: "Concesiones",
  comercios: "Parqueaderos",
};
const cards = [
  {
    key: "all",
    label: "Total",
    stat: "total",
    icon: WidgetsOutlined,
    color: "#1a73e8",
    background: "#edf4ff",
  },
  { key: "ok", label: "Activos", stat: "sinNovedad", icon: CheckCircleOutline, ...STATUS.ok },
  {
    key: "partial",
    label: "Falla parcial",
    stat: "posiblesFallo",
    icon: WarningAmberOutlined,
    ...STATUS.partial,
  },
  { key: "fail", label: "Con fallo", stat: "conFallo", icon: ErrorOutline, ...STATUS.fail },
];
const buttonSx = {
  textTransform: "none",
  borderRadius: 2,
  minHeight: 40,
  boxShadow: "none",
  fontSize: 13,
};
const CategoryBoard = ({ tipo, isOpen, onOpen, onClose, refreshFlag }) => {
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const { groups, loading, error, updatedAt, recargar, stats } = useTableroSac(tipo, refreshFlag);
  const unit = tipo === "eds" ? "Antenas" : tipo === "peajes" ? "Peajes / comercios" : "Terminales";
  const filtered = useMemo(() => {
    const term = search.trim().toLocaleLowerCase("es");
    return groups.filter(
      (group) =>
        (filter === "all" || group.status === filter) &&
        (!term ||
          [
            group.name,
            ...group.records.flatMap((item) => [
              item.comercio,
              item.terminalname,
              item.reader,
              item.antena,
              item.ciudad,
            ]),
          ].some((value) =>
            String(value ?? "")
              .toLocaleLowerCase("es")
              .includes(term)
          ))
    );
  }, [groups, filter, search]);
  const columns = [
    { Header: tipo === "peajes" ? "Concesión" : "Comercio", accessor: "name" },
    { Header: unit, accessor: "devices", align: "center" },
    { Header: "Última actividad", accessor: "last" },
    { Header: "Tiempo sin actividad", accessor: "elapsed" },
    { Header: "Estado", accessor: "status" },
    { Header: "Detalle", accessor: "action", align: "center" },
  ];
  const rows = filtered.map((group) => ({
    name: (
      <Typography variant="button" sx={{ textTransform: "none", fontWeight: 600 }}>
        {group.name}
      </Typography>
    ),
    devices: `${group.active}/${group.total} operativos`,
    last: formatDate(group.last),
    elapsed: formatMinutes(group.elapsed),
    status: <DashboardStatus status={group.status} />,
    action: (
      <Button
        aria-label={`Ver detalle de ${group.name}`}
        onClick={() => setSelected(group.id)}
        sx={buttonSx}
        endIcon={<ArrowForward fontSize="small" />}
      >
        Ver detalle
      </Button>
    ),
  }));
  const currentGroup = groups.find((group) => group.id === selected) || null;
  const toggleSummary = () => (isOpen ? onClose() : onOpen());
  const handleSummaryClick = (event) => {
    if (!event.currentTarget.contains(event.target)) return;
    if (
      event.target.closest(
        "button, a, input, select, textarea, label, [role='alert'], [data-dashboard-details]"
      )
    )
      return;
    toggleSummary();
  };
  return (
    <Paper
      component="section"
      aria-label={`Resumen de ${categories[tipo]}`}
      data-testid={`dashboard-${tipo}`}
      elevation={0}
      tabIndex={0}
      onClick={handleSummaryClick}
      onKeyDown={(event) => {
        if (event.target === event.currentTarget && (event.key === "Enter" || event.key === " ")) {
          event.preventDefault();
          toggleSummary();
        }
      }}
      sx={{
        cursor: "pointer",
        "&:focus-visible": { outline: "2px solid #1a73e8", outlineOffset: 3 },
        p: { xs: 1.5, sm: 2.5 },
        mb: 2.5,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 3,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.5, mb: 2 }}>
        <Box sx={{ flex: 1, minWidth: 180 }}>
          <Typography variant="h6">{tipo === "eds" ? "EDS" : categories[tipo]}</Typography>
          <Typography variant="caption" color="text.secondary">
            {updatedAt
              ? `Actualizado: ${formatDate(updatedAt)} · ${groups.reduce(
                  (total, group) => total + group.total,
                  0
                )} ${unit.toLowerCase()}`
              : loading
              ? "Consultando estadísticas…"
              : "Consulta pendiente"}
          </Typography>
        </Box>
        <Button
          startIcon={<Refresh />}
          disabled={loading}
          onClick={recargar}
          aria-label={`Actualizar ${categories[tipo]}`}
          sx={buttonSx}
        >
          {loading ? "Actualizando…" : "Actualizar"}
        </Button>
        <Button
          endIcon={isOpen ? <ExpandLess /> : <ExpandMore />}
          onClick={isOpen ? onClose : onOpen}
          aria-expanded={isOpen}
          aria-controls={`details-${tipo}`}
          aria-label={`${isOpen ? "Ocultar" : "Ver"} información de ${categories[tipo]}`}
          sx={buttonSx}
        >
          {isOpen ? "Ocultar información" : "Ver información"}
        </Button>
      </Box>
      {loading && (
        <LinearProgress
          aria-label={`Cargando ${categories[tipo]}`}
          sx={{
            mb: 2,
            width: "100%",
            position: "relative",
            overflow: "hidden",
            "& .MuiLinearProgress-bar": { bgcolor: "#1a73e8" },
          }}
        />
      )}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button disabled={loading} onClick={recargar} color="inherit">
              Reintentar
            </Button>
          }
        >
          {error}
          {updatedAt
            ? ". Se conserva la última consulta disponible."
            : ". No se pudo comprobar el estado de los comercios."}
        </Alert>
      )}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "repeat(2,minmax(0,1fr))", md: "repeat(4,minmax(0,1fr))" },
          gap: 2,
          mb: 2.5,
        }}
      >
        {cards.map((card) => (
          <Paper
            component="button"
            type="button"
            key={card.key}
            aria-pressed={isOpen && filter === card.key}
            onClick={() => {
              setFilter(card.key);
              onOpen();
            }}
            elevation={0}
            sx={{
              fontFamily: "inherit",
              textAlign: "left",
              p: { xs: 1.5, sm: 2.5 },
              cursor: "pointer",
              border: "1px solid",
              borderColor: isOpen && filter === card.key ? card.color : `${card.color}33`,
              boxShadow: isOpen && filter === card.key ? `0 0 0 1px ${card.color}` : "none",
              transition: "border-color 150ms, box-shadow 150ms",
              "&:hover": { borderColor: card.color },
              borderRadius: 2.5,
              bgcolor: card.background,
              display: "flex",
              gap: { xs: 1, sm: 2 },
              alignItems: "center",
              "&:focus-visible": { outline: `2px solid ${card.color}`, outlineOffset: 3 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                p: 1.25,
                color: card.color,
                bgcolor: card.background,
                borderRadius: 2,
              }}
            >
              <card.icon />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ color: "#627087" }}>
                {card.label === "Activo" ? "Activos" : card.label}
              </Typography>
              <Typography component="div" variant="h4" sx={{ color: "#27374d", mt: 0.25 }}>
                {loading && !updatedAt ? (
                  <Skeleton width={35} />
                ) : !updatedAt && error ? (
                  "—"
                ) : (
                  stats[card.stat]
                )}
              </Typography>
            </Box>
          </Paper>
        ))}
      </Box>
      {isOpen && (
        <Box id={`details-${tipo}`} data-dashboard-details sx={{ cursor: "default" }}>
          {stats.unknown > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {stats.unknown} {categories[tipo].toLowerCase()} sin estado global confirmado. Puedes
              consultarlos en el filtro «Sin información».
            </Alert>
          )}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Box
              sx={{
                p: 2.5,
                borderBottom: "1px solid",
                borderColor: "divider",
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 170 }}>
                <Typography variant="h6">{categories[tipo]}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {filtered.length} de {groups.length} registros ·{" "}
                  {groups.reduce((total, group) => total + group.total, 0)} {unit.toLowerCase()}
                </Typography>
              </Box>
              <TextField
                size="small"
                label="Buscar"
                placeholder="Nombre, terminal o ciudad…"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                sx={{ width: { xs: "100%", sm: 280 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                select
                size="small"
                label="Estado"
                value={filter}
                onChange={(event) => setFilter(event.target.value)}
                sx={{ minWidth: 165, "& .MuiInputBase-root": { height: 40 } }}
                SelectProps={{ SelectDisplayProps: { "aria-label": "Filtrar por estado" } }}
              >
                <MenuItem value="all">Todos</MenuItem>
                {Object.entries(STATUS).map(([key, value]) => (
                  <MenuItem value={key} key={key}>
                    {value.label}
                  </MenuItem>
                ))}
              </TextField>
              {(search || filter !== "all") && (
                <Button
                  onClick={() => {
                    setSearch("");
                    setFilter("all");
                  }}
                  sx={buttonSx}
                >
                  Limpiar
                </Button>
              )}
            </Box>
            <Box sx={dashboardTableStyles}>
              <DataTable
                table={{ columns, rows }}
                isSorted={false}
                canSearch={false}
                entriesPerPage={{ defaultValue: 10, entries: [5, 10, 25, 50] }}
                showTotalEntries
                noEndBorder
                autoResetPage={false}
                paginationResetKey={JSON.stringify([tipo, filter, search])}
              />
            </Box>
            {!loading && !error && !rows.length && (
              <Typography sx={{ p: 3 }} variant="body2">
                {groups.length
                  ? "No hay registros que coincidan con los filtros."
                  : "El servicio no devolvió registros para esta categoría."}
              </Typography>
            )}
          </Paper>
          <DashboardDetail group={currentGroup} tipo={tipo} onClose={() => setSelected(null)} />
        </Box>
      )}
    </Paper>
  );
};
CategoryBoard.propTypes = {
  tipo: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onOpen: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  refreshFlag: PropTypes.string,
};

export const TablaTableroSac = ({ refreshFlag }) => {
  const [openTipo, setOpenTipo] = useState(null);
  const [refresh, setRefresh] = useState(0);
  return (
    <Box sx={{ pb: 3 }}>
      <Paper
        elevation={0}
        sx={{
          mb: 2.5,
          p: { xs: 2, sm: 3 },
          borderRadius: 3,
          background: "linear-gradient(110deg,#12304d,#175d7a)",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <StorefrontOutlined />
        <Box sx={{ flex: 1, minWidth: 180 }}>
          <Typography variant="h5" color="inherit">
            Estado de comercios
          </Typography>
          <Typography variant="body2" color="inherit" sx={{ opacity: 0.85, mt: 0.5 }}>
            Resumen de EDS, concesiones y parqueaderos. Selecciona un indicador para ver el detalle.
          </Typography>
        </Box>
        <Button
          startIcon={<Refresh />}
          onClick={() => setRefresh((value) => value + 1)}
          sx={{
            ...buttonSx,
            bgcolor: "#fff",
            color: "#174b68",
            px: 2,
            "&:hover": { bgcolor: "#edf4ff" },
          }}
        >
          Actualizar todo
        </Button>
      </Paper>
      {Object.keys(categories).map((tipo) => (
        <CategoryBoard
          key={tipo}
          tipo={tipo}
          isOpen={openTipo === tipo}
          onOpen={() => setOpenTipo(tipo)}
          onClose={() => setOpenTipo(null)}
          refreshFlag={JSON.stringify([refreshFlag, refresh])}
        />
      ))}
    </Box>
  );
};
TablaTableroSac.propTypes = { refreshFlag: PropTypes.bool };
